<template>
  <div class="page-viewer" :class="{'editor-mode': editorMode}" :style="pageBackgroundCss">
    <div class="screen-height" :style="pageScaleCss" @mousedown="onMouseDown" ref="pagePreview">
      <template v-for="w in widgets">
        <component :class="{'widget-view': editorMode}" :is="widgetsByType[w.type].view" :widget="w" :key="w.id" :data-uuid="w.id"
                   @videoLoaded="videoLoaded" @userInteraction="data => $emit('userInteraction', data)"></component>
      </template>
      <size-control v-if="editorMode && selectedWidgetIsSizable" :selected-widget="selectedWidget"></size-control>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import consts from './consts';
import availableWidgets from './widgets/available-widgets';
import SizeControl from './SizeControl.vue';

export default {
  name: 'PageViewer',
  components: {
    SizeControl,
  },
  props: {
    editorMode: {
      type: Boolean,
      default: () => false,
    },
    pageContent: {
      type: Object,
      required: true,
    },
    shareMode: {
      type: Boolean,
      default: () => false,
    },
    selectedWidget: {
      type: Object,
    },
    pageId: {
      type: Number,
    },
  },
  data() {
    return {
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      movingWidget: null,
      pageHeight: 0,
      pageWidth: 0,
    };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  mounted() {
    this.onResize();
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },
  computed: {
    widgets() {
      return this.pageContent.widgets;
    },
    widgetsByType() {
      return availableWidgets();
    },
    selectedWidgetIsSizable() {
      return this.selectedWidget && _.has(this.selectedWidget, 'width') && _.has(this.selectedWidget, 'height');
    },
    pageScale() {
      const screenHeightRatio = this.pageHeight / consts.BASE_SCREEN_HEIGHT;
      const screenWidthRatio = this.pageWidth / consts.BASE_SCREEN_WIDTH;
      return Math.min(screenHeightRatio, screenWidthRatio);
    },
    pageScaleCss() {
      if (this.shareMode || this.editorMode) {
        return {};
      }
      const translateX = (this.pageWidth - consts.BASE_SCREEN_WIDTH) / 2;
      const translateY = (this.pageHeight - consts.BASE_SCREEN_HEIGHT) / 2;
      return {
        transform: `scale(${this.pageScale}) translate(${translateX}px, ${translateY}px)`,
      };
    },
    pageBackgroundCss() {
      const backgroundColor = this.pageContent.background;
      return backgroundColor ? { background: backgroundColor } : {};
    },
  },
  methods: {
    onResize() {
      // On desktop (page width greater than 430 we calculated the size of the page differently
      // This is not optimal since it duplicate logic in different place in the app, we decided to live with this duplication
      // instead of starting to extract it at the moment
      if (window.innerWidth >= 430) {
        this.pageWidth = 400;
        this.pageHeight = 0.9 * window.innerHeight - 35 - 20;
      } else {
        this.pageWidth = window.innerWidth;
        this.pageHeight = window.innerHeight;
      }
    },
    onMouseDown(e) {
      if (!this.editorMode) {
        return;
      }
      const { target } = e;
      this.movingWidget = this.findSelectedWidget(target);
      this.$emit('select', this.movingWidget);
      if (this.movingWidget) {
        this.startX = e.pageX;
        this.startY = e.pageY;
        this.originX = this.movingWidget.left;
        this.originY = this.movingWidget.top;

        document.addEventListener('mousemove', this.handleMouseMove, true);
        document.addEventListener('mouseup', this.handleMouseUp, true);
      }
    },
    handleMouseMove(e) {
      e.stopPropagation();
      e.preventDefault();

      const dx = e.pageX - this.startX;
      const dy = e.pageY - this.startY;
      const left = this.originX + dx;
      const top = this.originY + dy;

      this.movingWidget.left = left;
      this.movingWidget.top = top;
    },
    handleMouseUp() {
      document.removeEventListener('mousemove', this.handleMouseMove, true);
      document.removeEventListener('mouseup', this.handleMouseUp, true);
      this.movingWidget = null;
    },
    findSelectedWidget(el) {
      if (el === this.$refs.pagePreview) {
        return null;
      }
      const uuid = el.getAttribute('data-uuid');
      if (uuid) {
        return _.find(this.widgets, w => w.id === uuid);
      }
      return this.findSelectedWidget(el.parentElement);
    },
    videoLoaded(videoRef) {
      this.$emit('videoLoaded', { ref: videoRef, pageId: this.pageId });
    },
  },
};
</script>

<style lang="scss" scoped>
.page-viewer {
  position: relative;
  height: 100%;

  .screen-height {
    height: 100%;
  }

  &.editor-mode {
    box-sizing: content-box;
    border: 2px solid black;
    overflow: hidden;
  }

  .text-component {
    position: absolute;
  }

  .widget-view:hover {
    outline: 1px solid #ddd;
  }
}
</style>
